import Phaser from "phaser";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT } from "@yjsgames/phaser-ui";
import { DEPTH } from "../objects/depthLayers.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO, setButtonEnabled } from "./uiTokens.js";
import { t } from "../i18n/i18n.js";
import { GUIDE_QUEST_PANEL_H, GUIDE_QUEST_PANEL_GAP } from "./guideQuestSlotLayout.js";

export { GUIDE_QUEST_PANEL_H, GUIDE_QUEST_PANEL_GAP };

export interface GuideQuestPanelData {
  readonly visible: boolean;
  readonly title: string;
  readonly description: string;
  readonly progressText: string;
  readonly rewardIconKey: string;
  readonly rewardAmountText: string;
  readonly completed: boolean;
  readonly allDone: boolean;
}

export interface GuideQuestPanelOptions {
  readonly onClaim: () => void;
  readonly onGuide: () => void;
}

export interface GuideQuestPanelDebugSnapshot {
  readonly visible: boolean;
  readonly updateCount: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly action: "claim" | "guide" | "disabled";
  readonly title: string;
  readonly bgDepth: number;
  readonly titleDepth: number;
  readonly buttonDepth: number;
  readonly bgAlpha: number;
  readonly bgFillAlpha: number;
  readonly titleVisible: boolean;
  readonly titleAlpha: number;
  readonly titleDisplayIndex: number;
  readonly objectsAtCenter: readonly string[];
}

const CLAIM_BUTTON_W = 78;
const CLAIM_BUTTON_H = 22;
const CLAIM_BUTTON_RIGHT_PAD = 7;
const CLAIM_BUTTON_GAP = 8;
const PANEL_PAD_X = 10;
const PANEL_BG_ALPHA_ACTIVE = 0.68;
const PANEL_BG_ALPHA_DISABLED = 0.52;
const PANEL_STROKE_ALPHA_ACTIVE = 0.86;
const PANEL_STROKE_ALPHA_DISABLED = 0.56;
// 가이드퀘스트는 전투뷰 하위 오버레이이므로 패배/환생 dim보다 낮은 전용 depth를 쓴다.
const PANEL_BG_DEPTH = DEPTH.guideQuestPanelBg;
const PANEL_TEXT_DEPTH = DEPTH.guideQuestPanelText;
const PANEL_BUTTON_DEPTH = DEPTH.guideQuestPanelButton;

export class GuideQuestPanel {
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly title: Phaser.GameObjects.Text;
  private readonly description: Phaser.GameObjects.Text;
  private readonly claimButton: NineSliceButton;
  private buttonAction: "claim" | "guide" | "disabled" = "disabled";
  private updateCount = 0;
  private layout = { x: 0, y: 0, width: 0, height: GUIDE_QUEST_PANEL_H };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    opts: GuideQuestPanelOptions,
  ) {
    this.bg = scene.add
      .rectangle(x, y, width, GUIDE_QUEST_PANEL_H, PALETTE_NUM.buttonActiveBg, PANEL_BG_ALPHA_DISABLED)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE_NUM.accentAmber, PANEL_STROKE_ALPHA_DISABLED)
      .setDepth(PANEL_BG_DEPTH);
    this.title = scene.add
      .text(x + PANEL_PAD_X, y + GUIDE_QUEST_PANEL_H / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0, 0.5)
      .setDepth(PANEL_TEXT_DEPTH);
    this.description = scene.add
      .text(x + PANEL_PAD_X, y + GUIDE_QUEST_PANEL_H / 2, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textDim,
      })
      .setOrigin(0, 0)
      .setDepth(PANEL_TEXT_DEPTH);
    this.claimButton = new NineSliceButton(scene, 0, 0, {
      style: PRIMARY_BUTTON_STYLE,
      label: "",
      iconKey: "icon_gold",
      iconSize: 15,
      widthOverride: CLAIM_BUTTON_W,
      heightOverride: CLAIM_BUTTON_H,
    });
    this.claimButton.setDepth(PANEL_BUTTON_DEPTH);
    scene.add.existing(this.claimButton);
    this.claimButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
      if (this.buttonAction === "claim") opts.onClaim();
      else if (this.buttonAction === "guide") opts.onGuide();
    });
    this.setLayout(x, y, width);
  }

  update(data: GuideQuestPanelData): void {
    this.updateCount += 1;
    this.setVisible(data.visible);
    if (!data.visible) {
      this.buttonAction = "disabled";
      return;
    }
    const objective = data.description.replace(/[.。]$/, "");
    const compactObjective = `${objective.length > 26 ? data.title : objective} ${data.progressText}`;
    this.title.setText(
      data.completed
        ? `${t("guideQuest.claimed")} · ${compactObjective}`
        : compactObjective,
    );
    this.description.setText(data.description);
    this.description.setVisible(false).setActive(false);
    const claimReady = data.completed && !data.allDone;
    const guideReady = !data.completed && !data.allDone;
    const actionReady = claimReady || guideReady;
    this.bg
      .setFillStyle(PALETTE_NUM.buttonActiveBg, actionReady ? PANEL_BG_ALPHA_ACTIVE : PANEL_BG_ALPHA_DISABLED)
      .setStrokeStyle(1, PALETTE_NUM.accentAmber, actionReady ? PANEL_STROKE_ALPHA_ACTIVE : PANEL_STROKE_ALPHA_DISABLED);
    this.title.setColor(PALETTE.accentGold).setAlpha(actionReady ? 1 : 0.96);
    this.description.setColor(PALETTE.textDim).setAlpha(actionReady ? 0.82 : 0.48);
    this.buttonAction = claimReady ? "claim" : guideReady ? "guide" : "disabled";
    if (guideReady) {
      this.claimButton.setIconVisible(false);
      this.claimButton.setLabel(t("guideQuest.label"));
    } else {
      this.claimButton.setIcon(data.rewardIconKey);
      this.claimButton.setIconVisible(!data.allDone);
      this.claimButton.setLabel(claimReady ? t("guideQuest.claim") : data.rewardAmountText);
    }
    setButtonEnabled(this.claimButton, actionReady);
    this.claimButton.setAlpha(actionReady ? 1 : 0.38);
  }

  applyLanguage(): void {
    // 정적 문구 없음. 본문과 보상 수량은 update(data)가 갱신한다.
  }

  setVisible(visible: boolean): void {
    this.bg.setVisible(visible).setActive(visible);
    this.title.setVisible(visible).setActive(visible);
    this.description.setVisible(visible).setActive(visible);
    this.claimButton.setVisible(visible);
  }

  setLayout(x: number, y: number, width: number): void {
    const claimButtonX = x + width - CLAIM_BUTTON_RIGHT_PAD - CLAIM_BUTTON_W / 2;
    const textWidth = Math.max(110, claimButtonX - CLAIM_BUTTON_W / 2 - CLAIM_BUTTON_GAP - x - PANEL_PAD_X);
    this.layout = { x, y, width, height: GUIDE_QUEST_PANEL_H };
    this.bg.setPosition(x, y).setSize(width, GUIDE_QUEST_PANEL_H);
    this.title.setPosition(x + PANEL_PAD_X, y + GUIDE_QUEST_PANEL_H / 2).setWordWrapWidth(textWidth);
    this.description.setPosition(x + PANEL_PAD_X, y + GUIDE_QUEST_PANEL_H / 2).setWordWrapWidth(textWidth);
    this.claimButton.setPosition(claimButtonX, y + GUIDE_QUEST_PANEL_H / 2);
  }

  debugSnapshot(): GuideQuestPanelDebugSnapshot {
    const centerX = this.layout.x + this.layout.width / 2;
    const centerY = this.layout.y + this.layout.height / 2;
    return {
      visible: this.bg.visible,
      updateCount: this.updateCount,
      x: this.layout.x,
      y: this.layout.y,
      width: this.layout.width,
      height: this.layout.height,
      action: this.buttonAction,
      title: this.title.text,
      bgDepth: this.bg.depth,
      titleDepth: this.title.depth,
      buttonDepth: this.claimButton.depth,
      bgAlpha: this.bg.alpha,
      bgFillAlpha: this.bg.fillAlpha,
      titleVisible: this.title.visible,
      titleAlpha: this.title.alpha,
      titleDisplayIndex: this.bg.scene.children.list.indexOf(this.title),
      objectsAtCenter: this.objectsAt(centerX, centerY),
    };
  }

  /** 우측 action 버튼 사각형. 미완료면 가이드 버튼, 완료면 보상 수령 버튼 위치다. */
  actionButtonRect(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.claimButton.x - CLAIM_BUTTON_W / 2,
      y: this.claimButton.y - CLAIM_BUTTON_H / 2,
      width: CLAIM_BUTTON_W,
      height: CLAIM_BUTTON_H,
    };
  }

  private objectsAt(x: number, y: number): readonly string[] {
    const list = this.bg.scene.children.list;
    const hits: string[] = [];
    list.forEach((obj, index) => {
      const candidate = obj as Phaser.GameObjects.GameObject & {
        visible?: boolean;
        depth?: number;
        alpha?: number;
        type?: string;
        text?: string;
        getBounds?: () => Phaser.Geom.Rectangle;
      };
      if (candidate.visible === false || !candidate.getBounds) return;
      const b = candidate.getBounds();
      if (!b.contains(x, y)) return;
      hits.push(
        `${index}:${candidate.type ?? obj.constructor.name}:d${candidate.depth ?? 0}:a${candidate.alpha ?? 1}:${String(candidate.text ?? "").slice(0, 12)}`,
      );
    });
    return hits.slice(-12);
  }
}
